import { Link } from 'react-router-dom';
import { useReveal } from '../../hooks/useReveal';

interface HeroProps {
  title: React.ReactNode;
  subtitle?: string;
  description?: string;
  bgImage?: string;
  gradient?: string;
  primaryBtn?: { text: string; link: string; icon?: string };
  secondaryBtn?: { text: string; link: string; icon?: string };
  children?: React.ReactNode;
}

export default function Hero({
  title,
  subtitle,
  description,
  bgImage,
  gradient = "from-black via-gray-900 to-black",
  primaryBtn,
  secondaryBtn,
  children
}: HeroProps) {
  const { isVisible, ref } = useReveal(0.1);

  return (
    <section ref={ref as any} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        {bgImage && (
          <img 
            src={bgImage} 
            className="w-full h-full object-cover opacity-40" 
            alt="Hero Background" 
          />
        )}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-90`}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {subtitle && (
            <div className="mb-8">
              <span className="inline-block px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full text-sm font-medium border border-white/20 text-orange-400">
                {subtitle}
              </span>
            </div>
          )}
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-white">
            {title}
          </h1>
          
          {description && (
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              {description}
            </p>
          )}
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            {primaryBtn && (
              <Link 
                to={primaryBtn.link}
                className="btn-primary"
              >
                <span className="flex items-center gap-3">
                  {primaryBtn.icon && <i className={primaryBtn.icon}></i>}
                  {primaryBtn.text}
                </span>
              </Link>
            )}
            
            {secondaryBtn && (
              <Link 
                to={secondaryBtn.link}
                className="btn-secondary"
              >
                <span className="flex items-center gap-3">
                  {secondaryBtn.icon && <i className={secondaryBtn.icon}></i>}
                  {secondaryBtn.text}
                </span>
              </Link>
            )}
          </div>
          {children}
        </div>
      </div>
    </section>
  );
}